import { useCallback, useEffect, useRef } from 'react'

import {
  AppId,
  getIsContainerBridgeAvailable
} from '@inno-tools/container-bridge'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'
import { useNavigate } from 'react-router-dom'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { useMediaQuery } from 'shared/hooks/useMediaQuery'
import { usePreferencesStore } from 'shared/stores/preferencesStore'
import { getWindowIframe } from 'shared/utils/getWindowIframe'

import { normalizeUrl } from '../components/Window/components/WindowContent/utils'
import { useContainerBridge } from '../components/Window/hooks/useContainerBridge'

export const useWindowManager = () => {
  const activeWindow = useWindowStore(state =>
    state.windows.find(w => w.id === state.activeWindowId)
  )
  const minimizeAllWindows = useWindowStore(state => state.minimizeAllWindows)
  const closeAllWindows = useWindowStore(state => state.closeAllWindows)
  const { isMobile } = useMediaQuery()
  const navigate = useNavigate()

  const setActiveWindow = useWindowStore(state => state.setActiveWindow)
  const addToWindowHistory = useWindowStore(state => state.addToWindowHistory)
  const setCurrentNavigationIndex = useWindowStore(
    state => state.setCurrentNavigationIndex
  )
  const openWindow = useWindowStore(state => state.openWindow)
  const updateWindow = useWindowStore(state => state.updateWindow)
  const closeWindow = useWindowStore(state => state.closeWindow)

  const { apps } = useAvailableApps()

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const { messages } = useContainerBridge(iframeRef)

  useEffect(() => {
    iframeRef.current = activeWindow ? getWindowIframe(activeWindow.id) : null
  }, [activeWindow])

  const openAppWindow = useCallback(
    (appId: AppId, url?: string) => {
      // unmount any mount on the same node before remounting
      // so that when props of the app changes, it won't cause duplicate instances
      const existingWindow = useWindowStore
        .getState()
        .windows.find(w => w.id === appId)

      const openingMode = FEATURE_FLAGS.FORCE_DETACHED_MODE
        ? 'detached'
        : usePreferencesStore.getState().defaultAppOpeningMode

      if (isMobile || openingMode === 'detached') {
        const normalizedUrl = normalizeUrl(url ?? apps[appId].path)
        navigate(normalizedUrl ?? apps[appId].path)
        return
      }
      if (!existingWindow) {
        openWindow({
          id: appId,
          name: apps[appId].name,
          label: apps[appId].label,
          icon: apps[appId].icon,
          color: '#000',
          url: url ?? apps[appId].path,
          isMaximized: openingMode === 'maximized'
        })
      } else if (existingWindow.isMinimized) {
        updateWindow(appId, {
          isMinimized: false,
          isVisible: true
        })
        setActiveWindow(appId)
      } else {
        setActiveWindow(appId)
      }

      // point the message to the target window iframe,
      // not the currently active one!
      // useEffect updates the iframeRef, but there was a race condition
      // so I made sure it is updated here
      const targetIframe = existingWindow ? getWindowIframe(appId) : null
      iframeRef.current = targetIframe

      if (
        existingWindow &&
        getIsContainerBridgeAvailable(targetIframe?.contentWindow)
      ) {
        messages.postNavigateMessage(url ?? apps[appId].path, appId)
      } else {
        addToWindowHistory(appId, url ?? apps[appId].path)
        if (existingWindow) {
          setCurrentNavigationIndex(
            appId,
            (currentIndex: number) => currentIndex + 1
          )
        }
      }
    },
    [
      isMobile,
      apps,
      navigate,
      openWindow,
      updateWindow,
      setActiveWindow,
      addToWindowHistory,
      setCurrentNavigationIndex,
      messages
    ]
  )

  function openComponentAsWindow(...args: Parameters<typeof openWindow>) {
    openWindow(...args)
  }

  const resolveAppId = useCallback(
    (appId: string) => {
      const target = appId.trim().toLowerCase()
      if (!target) return null
      return (
        Object.values(apps).find(a => String(a.id).toLowerCase() === target) ??
        null
      )
    },
    [apps]
  )

  const openAppWindowByAppId = useCallback(
    (appId: string, subPath?: string | null) => {
      const app = resolveAppId(appId)
      if (!app) return false
      const trimmed = subPath?.trim()
      // Three sub-path shapes: query-only (`?folderId=1`), absolute
      // sub-route (`/locate-item/1`), or bare sub-route (`locate-item/1`).
      // Only the last needs a `/` prepended.
      const normalizedSub = trimmed
        ? trimmed.startsWith('?') || trimmed.startsWith('/')
          ? trimmed
          : `/${trimmed}`
        : ''
      const targetUrl = normalizedSub ? `${app.path}${normalizedSub}` : app.path
      openAppWindow(app.id as AppId, targetUrl)
      return true
    },
    [resolveAppId, openAppWindow]
  )

  const closeAppWindowByAppId = useCallback(
    (appId?: string | null) => {
      const targetId = appId
        ? (resolveAppId(appId)?.id ?? null)
        : (activeWindow?.id ?? null)
      if (!targetId) return false
      closeWindow(targetId)
      return true
    },
    [resolveAppId, activeWindow?.id, closeWindow]
  )

  return {
    closeAllWindows,
    minimizeAllWindows,
    openAppWindow,
    openAppWindowByAppId,
    closeAppWindowByAppId,
    openComponentAsWindow
  }
}
